import { Callable, Id, Optional } from '@vimeo/core/types/generic';

import { TFirebaseReference, TFirebaseSnapshot } from '../../../../types/firebase';
import { IInteractionApplicationUser } from '../../../../types/interaction_room';
import { IPoll, TPollsList } from '../../../../types/interaction_tools';
import { getRefValue, offRefEvent, onRefEvent } from '../../../../utils/firebase/cruds';
import { collectPollInteractionErrors } from '../../../../utils/firebase/interaction_objects.validators';
import { transformPoll } from '../../../../utils/firebase/transform';
import { queryInteractionUserSync } from '../../../../utils/store/interaction';
import {
  checkErrorsAndTrack,
  ELiveErrorCategory,
  trackLiveError,
} from '../../../../utils/tracking';
import { PollManager } from './PollManager';

/**
 * Hydrate active poll ID on initial load to initialize data.
 */
export async function hydrateActivePoll(
  manager: PollManager,
  pollActiveMetaRef: TFirebaseReference,
): Promise<void> {
  manager.log.info('Hydrating active poll:', pollActiveMetaRef.toString());

  try {
    const activePollId: Optional<Id> = await getRefValue(manager.firebase, pollActiveMetaRef);

    if (activePollId) {
      manager.log.info('Active poll hydrated:', activePollId);
      manager.setContext({ activePollId });
    }
  } catch (error) {
    trackLiveError(error, {
      method: 'hydrateActivePoll',
      category: ELiveErrorCategory.INTERACTION,
    });
  }
}

/**
 * Hydrate polls list.
 */
export async function hydratePollsList(
  manager: PollManager,
  pollsRef: TFirebaseReference,
): Promise<void> {
  manager.log.info('Hydrating polls list:', pollsRef.toString());

  try {
    const interactionUser: Optional<IInteractionApplicationUser> =
      queryInteractionUserSync(manager);
    const currentUserId: string = String(interactionUser?.id);

    const pollsList: Optional<TPollsList> = await getRefValue(manager.firebase, pollsRef);

    if (pollsList) {
      const nextPollsList: TPollsList = Object.entries(pollsList)
        .map(([key, it]) => transformPoll(key, it, currentUserId))
        .filter((it) =>
          checkErrorsAndTrack({
            data: it,
            possibleErrors: collectPollInteractionErrors(it),
            methodName: 'hydratePollsList',
          }),
        )
        .reduce((acc, it) => {
          acc[it.id] = it;
          return acc;
        }, {});

      manager.log.info('Polls hydrated:', pollsList);
      manager.setContext({ polls: nextPollsList });
    }
  } catch (error) {
    trackLiveError(error, {
      method: 'hydrateActivePoll',
      category: ELiveErrorCategory.INTERACTION,
    });
  }
}

/**
 * Subscribe to active poll ID and update state based on selected poll.
 */
export function subscribeToActivePoll(
  manager: PollManager,
  pollActiveIdMetaRef: TFirebaseReference,
): Callable {
  const onActivePollChanged = (snapshot: TFirebaseSnapshot) => {
    const activePollId: Optional<string> = snapshot.val();

    if (activePollId !== manager.context.activePollId) {
      manager.log.info('Active poll changed:', activePollId);
      manager.setContext({ activePollId });
    }
  };

  onRefEvent(manager.firebase, pollActiveIdMetaRef, 'value', onActivePollChanged, 'active-poll-id');
  return () => offRefEvent(manager.firebase, pollActiveIdMetaRef, 'value', onActivePollChanged);
}

/**
 * Subscribe to list of polls and handle whole collection values changes.
 */
export function subscribeToPolls(manager: PollManager, pollListRef: TFirebaseReference): Callable {
  const interactionUser: Optional<IInteractionApplicationUser> = queryInteractionUserSync(manager);
  const currentUserId: string = String(interactionUser?.id);

  const onPollAdded = (snapshot: TFirebaseSnapshot) => {
    const { polls } = manager.context;
    const poll: IPoll = transformPoll(snapshot.key as string, snapshot.val(), currentUserId);

    /**
     * In case if poll is already in collection, skip addition.
     * In case if poll is corrupted, skip addition.
     */
    if (
      !polls[snapshot.key as string] &&
      checkErrorsAndTrack({
        data: poll,
        possibleErrors: collectPollInteractionErrors(poll),
        methodName: 'onPollAdded',
      })
    ) {
      const nextPolls: TPollsList = { ...polls, [poll.id]: poll };

      manager.log.info('Loaded poll:', poll.id);
      manager.setContext({ polls: nextPolls });
    }
  };

  const onPollDeleted = (snapshot: TFirebaseSnapshot) => {
    const { polls } = manager.context;
    const pollId: string = snapshot.key as string;

    const nextPolls: TPollsList = { ...polls };
    delete nextPolls[pollId];

    manager.log.info('Removed poll:', pollId);
    manager.setContext({
      polls: nextPolls,
    });
  };

  const onPollUpdated = (snapshot: TFirebaseSnapshot) => {
    const { polls } = manager.context;
    const poll: IPoll = transformPoll(snapshot.key as string, snapshot.val(), currentUserId);

    if (
      checkErrorsAndTrack({
        data: poll,
        possibleErrors: collectPollInteractionErrors(poll),
        methodName: 'onPollUpdated',
      })
    ) {
      const nextPolls: TPollsList = { ...polls, [poll.id]: poll };

      manager.log.info('Updated poll:', poll.id, poll);

      manager.setContext({
        polls: nextPolls,
      });
    }
  };

  onRefEvent(manager.firebase, pollListRef, 'child_added', onPollAdded, 'polls-list');
  onRefEvent(manager.firebase, pollListRef, 'child_removed', onPollDeleted, 'polls-list');
  onRefEvent(manager.firebase, pollListRef, 'child_changed', onPollUpdated, 'polls-list');

  return () => {
    offRefEvent(manager.firebase, pollListRef, 'child_added', onPollAdded);
    offRefEvent(manager.firebase, pollListRef, 'child_removed', onPollDeleted);
    offRefEvent(manager.firebase, pollListRef, 'child_changed', onPollUpdated);
  };
}
